import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, Image, Type, Building2, Globe, CalendarPlus, Link2, Minus, Save, Trash2, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CANVAS_W = 600;

const PALETTE = [
  { type: "logo", label: "Logo", icon: Image },
  { type: "name", label: "Name & title", icon: Type },
  { type: "company", label: "Company details", icon: Building2 },
  { type: "weblink", label: "Website link", icon: Globe },
  { type: "cta", label: "Book an event button", icon: CalendarPlus },
  { type: "link", label: "Extra link", icon: Link2 },
  { type: "divider", label: "Divider line", icon: Minus },
  { type: "text", label: "Free text", icon: Type },
];

let uid = 1;
const nid = () => `b${Date.now()}${uid++}`;

const defaultsFor = (type, user) => {
  const origin = window.location.origin;
  switch (type) {
    case "logo": return { props: { url: "", link: origin, height: 64 } };
    case "name": return { props: { name: user?.name || "Your name", title: "Director", color: "#0F2547", sub_color: "#64748b", size: 16 } };
    case "company": return { props: { company: "Wales Event Medical Services", phone: "", email: user?.email || "", color: "#64748b", size: 12 } };
    case "weblink": return { props: { text: origin.replace(/^https?:\/\//, ""), url: origin, color: "#005EB8", size: 12 } };
    case "cta": return { props: { text: "Book your next event with us", url: `${origin}/intake`, bg: "#005EB8", color: "#ffffff" } };
    case "link": return { props: { text: "See what we cover", url: `${origin}/services`, color: "#00A499", size: 12 } };
    case "divider": return { props: { color: "#e2e8f0", width: 220 } };
    default: return { props: { text: "Registered medical event cover across Wales", color: "#64748b", size: 11 } };
  }
};

const estH = (b) => {
  const p = b.props || {};
  switch (b.type) {
    case "logo": return p.url ? (Number(p.height) || 64) : 34;
    case "name": { const s = Number(p.size) || 16; return s * 1.4 + (p.title ? s * 1.2 : 0); }
    case "company": { const s = Number(p.size) || 12; const n = [p.company, p.phone, p.email].filter((x) => x && String(x).trim()).length; return s * 1.5 * Math.max(n, 1); }
    case "weblink": case "link": return (Number(p.size) || 12) * 1.6;
    case "cta": return 40;
    case "divider": return 4;
    default: { const s = Number(p.size) || 11; return s * 1.5 * Math.max((p.text || "").split("\n").length, 1); }
  }
};

const BlockView = ({ b }) => {
  const p = b.props || {};
  const font = "Arial, Helvetica, sans-serif";
  switch (b.type) {
    case "logo":
      return p.url
        ? <img src={p.url} alt="" style={{ height: Number(p.height) || 64, display: "block" }} data-testid={`sigblock-logo-img-${b.id}`} />
        : <div className="text-[11px] text-slate-400 border border-dashed border-slate-300 rounded px-3 py-2 bg-white">Logo — upload it in the panel</div>;
    case "name": {
      const s = Number(p.size) || 16;
      return (
        <div style={{ fontFamily: font, lineHeight: 1.3 }}>
          <span style={{ fontSize: s, fontWeight: "bold", color: p.color || "#0F2547" }}>{p.name || "Your name"}</span>
          {p.title && <><br /><span style={{ fontSize: Math.max(s - 3, 10), color: p.sub_color || "#64748b" }}>{p.title}</span></>}
        </div>
      );
    }
    case "company": {
      const s = Number(p.size) || 12;
      const lines = [p.company, p.phone, p.email].filter((x) => x && String(x).trim());
      return (
        <div style={{ fontFamily: font, fontSize: s, lineHeight: 1.5, color: p.color || "#64748b" }}>
          {lines.length ? lines.map((l, i) => <div key={i}>{l}</div>) : "Company details"}
        </div>
      );
    }
    case "weblink":
    case "link": {
      const s = Number(p.size) || 12;
      return (
        <div style={{ fontFamily: font }}>
          <span style={{ fontSize: s, fontWeight: "bold", color: p.color || "#005EB8", textDecoration: "underline" }}>{p.text || p.url || "Link"}</span>
        </div>
      );
    }
    case "cta":
      return (
        <span style={{ fontFamily: font, display: "inline-block", background: p.bg || "#005EB8", color: p.color || "#ffffff", fontSize: 13, fontWeight: "bold", padding: "10px 20px", borderRadius: 999 }}>
          {p.text || "Book your next event with us"}
        </span>
      );
    case "divider":
      return <div style={{ width: Number(p.width) || Number(b.w) || 220, height: 2, background: p.color || "#e2e8f0" }} />;
    default: {
      const s = Number(p.size) || 11;
      return <div style={{ fontFamily: font, fontSize: s, lineHeight: 1.5, color: p.color || "#64748b", whiteSpace: "pre-wrap" }}>{p.text || "Free text"}</div>;
    }
  }
};

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">{label}</span>
    {children}
  </label>
);

const txt = "w-full h-9 rounded-lg border border-slate-300 px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]";

export default function SignatureBuilder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [autoAppend, setAutoAppend] = useState(true);
  const [selId, setSelId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dragRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/mailbox/signature");
        if (data.blocks?.length) {
          setBlocks(data.blocks);
          setAutoAppend(data.auto_append !== false);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    const move = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
      if (!d.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
      d.moved = true;
      setBlocks((bs) => bs.map((b) => b.id === d.id
        ? { ...b, x: Math.max(0, Math.min(CANVAS_W - 40, Math.round(d.bx + dx))), y: Math.max(0, Math.round(d.by + dy)) }
        : b));
    };
    const up = () => { dragRef.current = null; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, []);

  const addBlock = (type) => {
    const d = defaultsFor(type, user);
    const bottom = blocks.reduce((m, b) => Math.max(m, b.y + estH(b)), 0);
    const b = { id: nid(), type, x: type === "cta" ? 0 : 0, y: bottom + 12, w: d.props.width || 0, props: d.props };
    setBlocks((bs) => [...bs, b]);
    setSelId(b.id);
  };

  const sel = blocks.find((b) => b.id === selId);
  const setProp = (k, v) => setBlocks((bs) => bs.map((b) => b.id === selId ? { ...b, props: { ...b.props, [k]: v } } : b));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/site/upload", fd);
      setProp("url", data.url);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/mailbox/signature", { blocks, auto_append: autoAppend });
      toast.success(data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const canvasH = Math.max(260, blocks.reduce((m, b) => Math.max(m, b.y + estH(b)), 0) + 40);

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="signature-builder">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/admin/emails")} data-testid="sig-back"
            className="h-11 w-11 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547]">Email signature builder</h1>
            <p className="text-xs text-slate-500">Drag blocks anywhere — it's added to the bottom of emails you send from the Email section</p>
          </div>
          <button onClick={save} disabled={saving} data-testid="sig-save"
            className="h-11 px-5 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center gap-2 transition-colors">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save signature
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 flex flex-col lg:flex-row gap-4 items-start">
        <aside className="w-full lg:w-52 shrink-0 bg-white rounded-2xl border border-slate-200 p-3 space-y-1.5" data-testid="sig-palette">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 px-1 pb-1">Add a block</p>
          {PALETTE.map((p) => (
            <button key={p.type} onClick={() => addBlock(p.type)} data-testid={`sig-add-${p.type}`}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 hover:border-[#005EB8] hover:text-[#005EB8] text-slate-700 text-xs font-bold inline-flex items-center gap-2 transition-colors">
              <p.icon className="h-4 w-4" /> {p.label}
            </button>
          ))}
          <label className="flex items-start gap-2 px-1 pt-3 cursor-pointer" data-testid="sig-auto-append">
            <input type="checkbox" checked={autoAppend} onChange={(e) => setAutoAppend(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#005EB8]" />
            <span className="text-[11px] font-semibold text-slate-600">Add this signature to the bottom of every email I send</span>
          </label>
        </aside>

        <section className="flex-1 min-w-0 w-full">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 overflow-x-auto">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">Preview — drag blocks into place</p>
            <div className="relative bg-white rounded-xl border border-slate-200 select-none"
              style={{ width: CANVAS_W, height: canvasH }} data-testid="sig-canvas"
              onClick={() => setSelId(null)}>
              {!loaded && <p className="absolute inset-0 grid place-items-center text-xs text-slate-400">Loading…</p>}
              {loaded && blocks.length === 0 && (
                <p className="absolute inset-0 grid place-items-center text-xs text-slate-400 px-6 text-center" data-testid="sig-empty">
                  Nothing here yet — add blocks from the left, then drag them into place
                </p>
              )}
              {blocks.map((b) => (
                <div key={b.id} data-testid={`sigblock-${b.id}`}
                  onPointerDown={(e) => { e.stopPropagation(); setSelId(b.id); dragRef.current = { id: b.id, startX: e.clientX, startY: e.clientY, bx: b.x, by: b.y, moved: false }; }}
                  onClick={(e) => { e.stopPropagation(); setSelId(b.id); }}
                  className={`absolute cursor-move rounded ${selId === b.id ? "ring-2 ring-[#005EB8] ring-offset-1" : "hover:ring-1 hover:ring-slate-300"}`}
                  style={{ left: b.x, top: b.y }}>
                  <BlockView b={b} />
                </div>
              ))}
            </div>
          </div>
        </section>

        <aside className="w-full lg:w-64 shrink-0 bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="sig-props">
          {!sel ? (
            <p className="text-xs text-slate-400" data-testid="sig-props-empty">Click a block to edit its text, links and colours.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-[#0F2547] capitalize">{PALETTE.find((p) => p.type === sel.type)?.label}</p>
                <button onClick={() => { setBlocks((bs) => bs.filter((b) => b.id !== selId)); setSelId(null); }} data-testid="sig-delete-block"
                  className="h-8 w-8 grid place-items-center rounded-lg text-[#D5281B] hover:bg-red-50 transition-colors" title="Remove block">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {sel.type === "logo" && (
                <>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} data-testid="sig-logo-file" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sig-logo-upload"
                    className="w-full h-10 rounded-lg border border-slate-300 hover:border-[#005EB8] text-xs font-bold text-[#005EB8] inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload logo
                  </button>
                  <Field label="Logo links to" testid="sig-prop-logo-link">
                    <input value={sel.props.link || ""} onChange={(e) => setProp("link", e.target.value)} className={txt} placeholder="https://…" data-testid="sig-logo-link" />
                  </Field>
                  <Field label="Logo height (px)" testid="sig-prop-logo-height">
                    <input type="number" min="24" max="160" value={sel.props.height || 64} onChange={(e) => setProp("height", Number(e.target.value))} className={txt} data-testid="sig-logo-height" />
                  </Field>
                </>
              )}
              {sel.type === "name" && (
                <>
                  <Field label="Name"><input value={sel.props.name || ""} onChange={(e) => setProp("name", e.target.value)} className={txt} data-testid="sig-name" /></Field>
                  <Field label="Job title"><input value={sel.props.title || ""} onChange={(e) => setProp("title", e.target.value)} className={txt} data-testid="sig-title" /></Field>
                  <Field label="Name colour"><input type="color" value={sel.props.color || "#0F2547"} onChange={(e) => setProp("color", e.target.value)} className="h-9 w-full rounded-lg border border-slate-300" data-testid="sig-name-color" /></Field>
                  <Field label="Text size"><input type="number" min="11" max="24" value={sel.props.size || 16} onChange={(e) => setProp("size", Number(e.target.value))} className={txt} data-testid="sig-name-size" /></Field>
                </>
              )}
              {sel.type === "company" && (
                <>
                  <Field label="Company name"><input value={sel.props.company || ""} onChange={(e) => setProp("company", e.target.value)} className={txt} data-testid="sig-company" /></Field>
                  <Field label="Phone"><input value={sel.props.phone || ""} onChange={(e) => setProp("phone", e.target.value)} className={txt} data-testid="sig-phone" /></Field>
                  <Field label="Email"><input value={sel.props.email || ""} onChange={(e) => setProp("email", e.target.value)} className={txt} data-testid="sig-company-email" /></Field>
                  <Field label="Colour"><input type="color" value={sel.props.color || "#64748b"} onChange={(e) => setProp("color", e.target.value)} className="h-9 w-full rounded-lg border border-slate-300" data-testid="sig-company-color" /></Field>
                </>
              )}
              {(sel.type === "weblink" || sel.type === "link" || sel.type === "cta") && (
                <>
                  <Field label="Button / link text"><input value={sel.props.text || ""} onChange={(e) => setProp("text", e.target.value)} className={txt} data-testid="sig-link-text" /></Field>
                  <Field label="Links to"><input value={sel.props.url || ""} onChange={(e) => setProp("url", e.target.value)} className={txt} placeholder="https://…" data-testid="sig-link-url" /></Field>
                  <Field label="Colour"><input type="color" value={(sel.type === "cta" ? sel.props.bg : sel.props.color) || "#005EB8"} onChange={(e) => setProp(sel.type === "cta" ? "bg" : "color", e.target.value)} className="h-9 w-full rounded-lg border border-slate-300" data-testid="sig-link-color" /></Field>
                </>
              )}
              {sel.type === "divider" && (
                <>
                  <Field label="Line colour"><input type="color" value={sel.props.color || "#e2e8f0"} onChange={(e) => setProp("color", e.target.value)} className="h-9 w-full rounded-lg border border-slate-300" data-testid="sig-divider-color" /></Field>
                  <Field label="Line width (px)"><input type="number" min="40" max="560" value={sel.props.width || 220} onChange={(e) => setProp("width", Number(e.target.value))} className={txt} data-testid="sig-divider-width" /></Field>
                </>
              )}
              {sel.type === "text" && (
                <>
                  <Field label="Text"><textarea rows={3} value={sel.props.text || ""} onChange={(e) => setProp("text", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]" data-testid="sig-text" /></Field>
                  <Field label="Colour"><input type="color" value={sel.props.color || "#64748b"} onChange={(e) => setProp("color", e.target.value)} className="h-9 w-full rounded-lg border border-slate-300" data-testid="sig-text-color" /></Field>
                </>
              )}
            </>
          )}
        </aside>
      </main>
    </div>
  );
}
